import {
  Pagination,
  Spinner,
  Text,
} from "@appquality/appquality-design-system";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useGetUsersMeCampaignsByCampaignIdBugsQuery } from "../../services/tryberApi";
import BugCard from "./BugCard";
import { EmptyState, LoadingContainer, ScrollPane } from "./styled";

const LIMIT = 25;

interface BugListProps {
  campaignId: string;
  currentBugId?: number;
}

const BugList = ({ campaignId, currentBugId }: BugListProps) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const selectedCardRef = useRef<HTMLDivElement>(null);
  const scrollPaneRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const currentPage =
    Number(new URLSearchParams(location.search).get("page")) || 1;

  const {
    selectedSeverity,
    selectedStatus,
    selectedTags,
    selectedReplicability,
    selectedBugType,
    search,
  } = useSelector((state: GeneralState) => state.bugShow);

  const filterBy: { [key: string]: string } = {};
  if (selectedSeverity?.value) {
    filterBy.severity = selectedSeverity.value.toString();
  }
  if (selectedStatus?.value) {
    filterBy.status = selectedStatus.value.toString();
  }
  if (selectedTags && selectedTags.length > 0 && selectedTags[0]?.value) {
    filterBy.tag = selectedTags[0].value.toString();
  }
  if (selectedReplicability?.value) {
    filterBy.replicability = selectedReplicability.value.toString();
  }
  if (selectedBugType?.value) {
    filterBy.type = selectedBugType.value.toString();
  }

  const goToPage = useCallback(
    (page: number) => {
      const params = new URLSearchParams(location.search);
      params.set("page", page.toString());
      history.replace({ search: params.toString() });
    },
    [history, location.search],
  );

  const { data, error, isLoading } =
    useGetUsersMeCampaignsByCampaignIdBugsQuery({
      campaignId,
      filterBy: Object.keys(filterBy).length > 0 ? filterBy : undefined,
      search: search && search.trim() !== "" ? search.trim() : undefined,
      limit: LIMIT,
      start: (currentPage - 1) * LIMIT,
    });

  const totalBugs = data?.total ?? 0;

  const localPrefix = i18n.language !== "en" ? `/${i18n.language}` : "";
  const maxPages = Math.ceil((data?.total ?? 0) / LIMIT);

  useEffect(() => {
    if (data && data.items && data.items.length > 0 && !currentBugId) {
      const currentParams = new URLSearchParams(location.search);
      if (!currentParams.has("page")) {
        currentParams.set("page", "1");
      }

      history.replace({
        pathname: `${localPrefix}/bugs/show/${data.items[0].id}`,
        search: currentParams.toString(),
      });
    }
  }, [data, currentBugId, history, localPrefix, location.search]);

  useEffect(() => {
    if (selectedCardRef.current && currentBugId) {
      selectedCardRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [currentBugId]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Send to first page when filters or search change
    goToPage(1);
  }, [
    selectedSeverity,
    selectedStatus,
    selectedTags,
    selectedReplicability,
    selectedBugType,
    search,
    goToPage,
  ]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <EmptyState>
        <Text className="aq-text-danger">
          {t("Error loading bugs. Please try again.")}
        </Text>
      </EmptyState>
    );
  }

  if (!data || !data.items || data.items.length === 0) {
    return (
      <EmptyState>
        <Text>{t("No bugs found.")}</Text>
      </EmptyState>
    );
  }

  return (
    <>
      <Text className="aq-mb-3">
        {t("Total bugs: {{totalBugs}}", { totalBugs })}
      </Text>
      <ScrollPane ref={scrollPaneRef}>
        {data.items.map((bug) => (
          <BugCard
            key={bug.id}
            ref={bug.id === currentBugId ? selectedCardRef : null}
            bug={bug}
            isSelected={bug.id === currentBugId}
            onClick={() =>
              history.push({
                pathname: `${localPrefix}/bugs/show/${bug.id}`,
                search: location.search,
              })
            }
          />
        ))}
      </ScrollPane>
      {maxPages > 1 && (
        <Pagination
          className="aq-pt-3"
          onPageChange={(page) => {
            goToPage(page);
            scrollPaneRef.current?.scrollTo({ top: 0, behavior: "smooth" });
          }}
          current={currentPage}
          maxPages={maxPages}
          mobileText={(current, total) =>
            t(`Page %current% / %total%`)
              .replace("%current%", current.toString())
              .replace("%total%", total?.toString() ?? "0")
          }
        />
      )}
    </>
  );
};

export default BugList;
